/**
 * Masks, effective permissions, draft edits, diffs and the shared entry words — S8 Task 8.2 (E5, finding S8-08).
 * A draft is a plain entry array in service order. A mask changes ONLY through `setMask` / `recalculateMask` (or the
 * one visible default `addNamedEntryDraft` adds to a scope that had none); editing a named entry never touches it, so a
 * restrictive mask is never widened as a side effect (NOTES «S8: live-probed facts» A4: the service keeps an explicit
 * mask exactly as sent).
 */
import type { StorageAclEntry, StorageAclPermissions, StorageAclScope } from '../../types/azureStorage';
import { aclEntryKey as keyOf } from './azureStorageAcl';

export type AclEntryKey = { scope: StorageAclScope; type: StorageAclEntry['type']; id?: string };
export type EffectiveEntry = { entry: StorageAclEntry; effective: StorageAclPermissions };
/** `removed` = the entry is gone from the ACL (`after` = no bits): its principal falls back to the owning-group / other classes. */
export type EffectiveChange = { entry: StorageAclEntry; before: StorageAclPermissions; after: StorageAclPermissions; change: 'widened' | 'narrowed' | 'mixed' | 'removed' };
export type AclDiff = { added: StorageAclEntry[]; removed: StorageAclEntry[]; changed: Array<{ before: StorageAclEntry; after: StorageAclEntry }> };

const NONE: StorageAclPermissions = { read: false, write: false, execute: false };
const and = (a: StorageAclPermissions, b: StorageAclPermissions): StorageAclPermissions => ({ read: a.read && b.read, write: a.write && b.write, execute: a.execute && b.execute });
const or = (a: StorageAclPermissions, b: StorageAclPermissions): StorageAclPermissions => ({ read: a.read || b.read, write: a.write || b.write, execute: a.execute || b.execute });
const same = (a: StorageAclPermissions, b: StorageAclPermissions): boolean => a.read === b.read && a.write === b.write && a.execute === b.execute;
const matches = (e: StorageAclEntry, key: AclEntryKey): boolean => keyOf(e) === keyOf(key);
/** Named users, named groups AND the owning group are limited by the mask; the owner and other are not. */
const isMasked = (e: StorageAclEntry): boolean => e.type === 'group' || (e.type === 'user' && !!e.id);

export function effectiveEntries(entries: readonly StorageAclEntry[]): EffectiveEntry[] {
  return entries.map((entry) => {
    const mask = entries.find((x) => x.scope === entry.scope && x.type === 'mask');
    return { entry, effective: mask && isMasked(entry) ? and(entry.permissions, mask.permissions) : entry.permissions };
  });
}

/** Union of a scope's named entries and owning group — used ONLY by Recalculate mask and by the first named entry of a mask-less scope. */
export function computeMask(entries: readonly StorageAclEntry[], scope: StorageAclScope): StorageAclPermissions {
  return entries.filter((e) => e.scope === scope && isMasked(e)).reduce((acc, e) => or(acc, e.permissions), NONE);
}

export const aclDraftFrom = (loaded: readonly StorageAclEntry[]): StorageAclEntry[] => loaded.map((e) => ({ ...e, permissions: { ...e.permissions } }));

export function editEntryPermissions(draft: readonly StorageAclEntry[], key: AclEntryKey, permissions: StorageAclPermissions): StorageAclEntry[] {
  return draft.map((e) => (matches(e, key) && e.type !== 'mask' ? { ...e, permissions: { ...permissions } } : e));
}

export function setMask(draft: readonly StorageAclEntry[], scope: StorageAclScope, permissions: StorageAclPermissions): StorageAclEntry[] {
  return draft.map((e) => (e.scope === scope && e.type === 'mask' ? { ...e, permissions: { ...permissions } } : e));
}

export const recalculateMask = (draft: readonly StorageAclEntry[], scope: StorageAclScope): StorageAclEntry[] => setMask(draft, scope, computeMask(draft, scope));

/** Service order inside a scope: owner, named users, owning group, named groups, mask, other. */
const rank = (e: StorageAclEntry): number => (e.scope === 'default' ? 10 : 0) + (e.type === 'user' ? (e.id ? 1 : 0) : e.type === 'group' ? (e.id ? 3 : 2) : e.type === 'mask' ? 4 : 5);
export const orderAclEntries = (entries: readonly StorageAclEntry[]): StorageAclEntry[] => entries.map((e, i) => ({ e, i })).sort((a, b) => rank(a.e) - rank(b.e) || a.i - b.i).map(({ e }) => e);

/** Adds a named entry; a scope that had no mask gets a VISIBLE computed one (the user sees and can change it before saving). */
export function addNamedEntryDraft(draft: readonly StorageAclEntry[], entry: StorageAclEntry): StorageAclEntry[] {
  const next = [...aclDraftFrom(draft), { ...entry, permissions: { ...entry.permissions } }];
  if (!next.some((e) => e.scope === entry.scope && e.type === 'mask')) next.push({ scope: entry.scope, type: 'mask', permissions: computeMask(next, entry.scope) });
  return orderAclEntries(next);
}

export const removeNamedEntryDraft = (draft: readonly StorageAclEntry[], key: AclEntryKey): StorageAclEntry[] => draft.filter((e) => !(key.id && matches(e, key)));

function classify(before: StorageAclPermissions, after: StorageAclPermissions): Exclude<EffectiveChange['change'], 'removed'> {
  const gained = (!before.read && after.read) || (!before.write && after.write) || (!before.execute && after.execute);
  const lost = (before.read && !after.read) || (before.write && !after.write) || (before.execute && !after.execute);
  return gained && lost ? 'mixed' : gained ? 'widened' : 'narrowed';
}

/**
 * Every principal entry whose EFFECTIVE permissions differ between two ACLs (the dialog's and the approval's preview),
 * then every principal entry `after` no longer has, flagged `removed` whatever its bits (review astra-A #3).
 */
export function diffEffective(before: readonly StorageAclEntry[], after: readonly StorageAclEntry[]): EffectiveChange[] {
  const old = new Map(effectiveEntries(before).map((x) => [keyOf(x.entry), x.effective]));
  const kept = new Set(after.map(keyOf));
  const out: EffectiveChange[] = [];
  for (const { entry, effective } of effectiveEntries(after)) {
    if (entry.type === 'mask') continue;
    const was = old.get(keyOf(entry)) ?? NONE;
    if (!same(was, effective)) out.push({ entry, before: was, after: effective, change: classify(was, effective) });
  }
  for (const { entry, effective } of effectiveEntries(before)) {
    if (entry.type !== 'mask' && !kept.has(keyOf(entry))) out.push({ entry, before: effective, after: NONE, change: 'removed' });
  }
  return out;
}

export function diffAcl(before: readonly StorageAclEntry[], after: readonly StorageAclEntry[]): AclDiff {
  const old = new Map(before.map((e) => [keyOf(e), e]));
  const next = new Map(after.map((e) => [keyOf(e), e]));
  return {
    added: after.filter((e) => !old.has(keyOf(e))),
    removed: before.filter((e) => !next.has(keyOf(e))),
    changed: after.filter((e) => old.has(keyOf(e)) && !same(old.get(keyOf(e))!.permissions, e.permissions)).map((e) => ({ before: old.get(keyOf(e))!, after: e })),
  };
}

const words = (p: StorageAclPermissions): string => [p.read && 'read', p.write && 'write', p.execute && 'execute'].filter(Boolean).join(', ') || 'no access';
const shortId = (id: string): string => (id.length > 12 ? `${id.slice(0, 8)}…` : id);

/**
 * "user 1b2c3d4e…", "owning group", "mask": who an entry names. A display name never replaces the object id (review opus
 * F6): a labelled entry reads "user ana@contoso.com (1b2c3d4e…)", so a consent text always shows the id that is written.
 */
export function describeAclPrincipal(entry: Pick<StorageAclEntry, 'type' | 'id'>, displayNames: Record<string, string> = {}): string {
  if (!entry.id) return entry.type === 'user' ? 'owning user' : entry.type === 'group' ? 'owning group' : entry.type;
  const label = displayNames[entry.id];
  return label ? `${entry.type} ${label} (${shortId(entry.id)})` : `${entry.type} ${shortId(entry.id)}`;
}

/** "user 1b2c3d4e…: read, execute (default for new children)" — the words the dialog, the MCP approval and the docs share. */
export function describeAclEntry(entry: StorageAclEntry, displayNames: Record<string, string> = {}): string {
  return `${describeAclPrincipal(entry, displayNames)}: ${words(entry.permissions)}${entry.scope === 'default' ? ' (default for new children)' : ''}`;
}
